// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * API endpoint for updating asset ownership information.
 * Provides functionality to record ownership transfers, including
 * sale prices and transfer dates.
 */
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { assetSchema } from '@/asset/api/types';
import { Currency } from '@/currency/api/types';
import { serializeOptionalCurrencyForAPI } from '@/currency/utilities/serialize';
import { serializeDateForAPI } from '@/utilities/date-time';
import { DateTime } from 'luxon';
import { z } from 'zod';

/**
 * Schema for validating the API response when updating asset ownership.
 * Transforms the response to provide a more intuitive structure with
 * the asset data separated from metadata.
 */
const assignAssetResponseSchema = z
  .object({ data: assetSchema, metadata: responseMetadataSchema })
  .transform(({ data, ...response }) => ({ ...response, asset: data }));

/**
 * Parameters required for updating asset ownership.
 * @typedef {Object} AssetOwnershipParameters
 * @property {string} customerId - Identifier of the new owner (customer)
 * @property {DateTime} date - Date when the ownership transfer occurred
 * @property {Currency} [salePrice] - Optional sale price (if ownership transfer involves a sale)
 */
interface AssetOwnershipParameters {
  salePrice?: Currency;
  customerId: string;
  date: DateTime;
}

/**
 * Updates the ownership information for a specific asset.
 * This function handles the API call to record ownership transfers and validates
 * the response data using Zod schemas. It can be used to record both
 * initial ownership assignments and subsequent transfers.
 *
 * @param key - Unique identifier of the asset to update ownership for
 * @param parameters - Object containing the ownership transfer details
 * @returns Promise resolving to the updated asset data and request metadata
 * @throws Will throw an error if the API request fails or if the response data is invalid
 */
export async function updateAssetOwnership(
  key: string,
  parameters: AssetOwnershipParameters
) {
  const path = `/v1/asset/${key}/ownership`;
  const response = await api.post(path, {
    salePrice: serializeOptionalCurrencyForAPI(parameters.salePrice),
    customerId: parameters.customerId,
    date: serializeDateForAPI(parameters.date),
  });

  return parseResponse(assignAssetResponseSchema, response.data, {
    schemaName: 'UpdateAssetOwnership',
    endpoint: path,
  });
}
